import './SiderContent.scss';

import { useMenu } from '@refinedev/core';
import { Menu } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { RESOURCES } from '@/Resources';

import AcronexThemedTitle from '../AcronexThemedTitle';

const SiderContent = () => {
  const { t } = useTranslation();
  const { isMediumScreen } = useUiContext();
  const { hasPermission } = usePermissionContext();
  const { pathname } = useLocation();
  const { menuItems } = useMenu();
  const selectedKey = useMemo(() => {
    const flatItems = menuItems.flatMap(item => {
      if (item.children?.length > 0) {
        return [
          item,
          ...item.children.map(child => ({
            key: child.key,
            route: child.route,
            label: child.label,
            icon: child.icon,
          })),
        ];
      }

      return {
        key: item.key,
        route: item.route,
        label: item.label,
        icon: item.icon,
      };
    });
    const menuItemSelected = flatItems.find(
      menuItem => menuItem.route && pathname.includes(menuItem.route),
    )?.key;

    return menuItemSelected ? [menuItemSelected] : [];
  }, [menuItems, pathname]);
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      if (!hasChildren) {
        // Leaf item: show if any permission on its resource
        return hasPermission('list', item.name as RESOURCES);
      }

      // Parent with children: show if at least one child is allowed
      return item.children?.some(child => hasPermission('list', child.name as RESOURCES));
    });
  }, [hasPermission, menuItems]);

  return (
    <div className="SiderContent">
      <div className="SiderContentHeader">
        <AcronexThemedTitle />
      </div>
      <div className={`SiderContentBody ${isMediumScreen ? 'SiderContentBodyMobile' : ''}`}>
        <Menu
          mode="inline"
          inlineIndent={16}
          className="SiderContentMenu"
          selectedKeys={selectedKey}
          items={filteredMenuItems.map(item => ({
            key: item.key,
            icon: item.icon,
            label: <Link to={item.route ?? '#'}>{item.label && t(item.label)}</Link>,
            children:
              item.children && item.children.length > 0
                ? item.children
                    .filter(child => hasPermission('list', child.name as RESOURCES))
                    .map(child => ({
                      key: child.key,
                      icon: child.icon,
                      label: <Link to={child.route ?? '#'}>{child.label && t(child.label)}</Link>,
                    }))
                : undefined,
          }))}
        />
      </div>
    </div>
  );
};

export default SiderContent;
